/**
 * AI Settings Service
 * TASK-335: AI Assistant Configuration & Restrictions
 *
 * Handles AI configuration, topic restrictions, interaction logs,
 * and usage statistics for the admin panel.
 */

import { httpClient } from '../../lib/httpClient';
import { logger } from '../../utils/logger';
import type {
  AISettings,
  AISettingsUpdateData,
  TopicRestriction,
  TopicRestrictionCreateData,
  AIInteractionLog,
  AIInteractionLogListResponse,
  AIInteractionLogFilter,
  AIUsageStats,
  AIFeatureStatus,
} from '../../types/admin';

class AISettingsService {
  private readonly basePath = '/api/admin/ai';

  // ==================== AI Settings CRUD ====================

  /**
   * Get AI settings for the account
   */
  async getAISettings(): Promise<AISettings> {
    try {
      const data = await httpClient.get<AISettings>(`${this.basePath}/settings`);
      return data;
    } catch (error) {
      logger.error('Error fetching AI settings:', error);
      throw error;
    }
  }

  /**
   * Update AI settings
   */
  async updateAISettings(updates: AISettingsUpdateData): Promise<AISettings> {
    try {
      const data = await httpClient.put<AISettings>(`${this.basePath}/settings`, updates);
      return data;
    } catch (error) {
      logger.error('Error updating AI settings:', error);
      throw error;
    }
  }

  /**
   * Reset AI settings to EU AI Act / GDPR compliant defaults
   */
  async resetAISettingsToDefaults(): Promise<AISettings> {
    try {
      const data = await httpClient.post<AISettings>(`${this.basePath}/settings/reset-defaults`);
      return data;
    } catch (error) {
      logger.error('Error resetting AI settings:', error);
      throw error;
    }
  }

  // ==================== Topic Restrictions ====================

  /**
   * Get all topic restrictions
   */
  async getTopicRestrictions(): Promise<TopicRestriction[]> {
    try {
      const data = await httpClient.get<TopicRestriction[]>(`${this.basePath}/restrictions`);
      return data;
    } catch (error) {
      logger.error('Error fetching topic restrictions:', error);
      throw error;
    }
  }

  /**
   * Add a new topic restriction
   */
  async addTopicRestriction(restriction: TopicRestrictionCreateData): Promise<TopicRestriction> {
    try {
      const data = await httpClient.post<TopicRestriction>(
        `${this.basePath}/restrictions`,
        restriction
      );
      return data;
    } catch (error) {
      logger.error('Error adding topic restriction:', error);
      throw error;
    }
  }

  /**
   * Remove a topic restriction
   */
  async removeTopicRestriction(id: string): Promise<void> {
    try {
      await httpClient.delete(`${this.basePath}/restrictions/${id}`);
    } catch (error) {
      logger.error('Error removing topic restriction:', error);
      throw error;
    }
  }

  // ==================== Interaction Logs ====================

  /**
   * Get paginated AI interaction logs
   */
  async getInteractionLogs(filter?: AIInteractionLogFilter): Promise<AIInteractionLogListResponse> {
    try {
      const params = new URLSearchParams();

      if (filter?.page) params.append('page', filter.page.toString());
      if (filter?.page_size) params.append('page_size', filter.page_size.toString());
      if (filter?.user_id) params.append('user_id', filter.user_id);
      if (filter?.interaction_type) params.append('interaction_type', filter.interaction_type);
      if (filter?.risk_level) params.append('risk_level', filter.risk_level);
      if (filter?.pii_detected !== undefined)
        params.append('pii_detected', filter.pii_detected.toString());
      if (filter?.date_from) params.append('date_from', filter.date_from);
      if (filter?.date_to) params.append('date_to', filter.date_to);

      const queryString = params.toString();
      const url = queryString ? `${this.basePath}/logs?${queryString}` : `${this.basePath}/logs`;

      const data = await httpClient.get<AIInteractionLogListResponse>(url);
      return data;
    } catch (error) {
      logger.error('Error fetching interaction logs:', error);
      throw error;
    }
  }

  /**
   * Get a specific interaction log entry
   */
  async getInteractionLog(id: string): Promise<AIInteractionLog> {
    try {
      const data = await httpClient.get<AIInteractionLog>(`${this.basePath}/logs/${id}`);
      return data;
    } catch (error) {
      logger.error('Error fetching interaction log:', error);
      throw error;
    }
  }

  // ==================== Usage Stats ====================

  /**
   * Get AI usage statistics
   */
  async getUsageStats(days: number = 30): Promise<AIUsageStats> {
    try {
      const data = await httpClient.get<AIUsageStats>(
        `${this.basePath}/usage-stats?days=${days}`
      );
      return data;
    } catch (error) {
      logger.error('Error fetching usage stats:', error);
      throw error;
    }
  }

  // ==================== Feature Status ====================

  /**
   * Get AI feature status (for other parts of the app)
   */
  async getFeatureStatus(): Promise<AIFeatureStatus> {
    try {
      const data = await httpClient.get<AIFeatureStatus>(`${this.basePath}/feature-status`);
      return data;
    } catch (error) {
      logger.error('Error fetching feature status:', error);
      throw error;
    }
  }

  // ==================== Helper Methods ====================

  /**
   * Format retention days for display
   */
  formatRetentionDays(days: number): string {
    if (days < 365) {
      return `${days} days`;
    }
    const years = Math.floor(days / 365);
    const remainingDays = days % 365;
    if (remainingDays === 0) {
      return `${years} year${years > 1 ? 's' : ''}`;
    }
    return `${years} year${years > 1 ? 's' : ''} ${remainingDays} days`;
  }

  /**
   * Get compliance status summary
   */
  getComplianceStatus(settings: AISettings): {
    euAiActCompliant: boolean;
    gdprCompliant: boolean;
    issues: string[];
  } {
    const issues: string[] = [];

    // EU AI Act checks
    if (settings.investment_advice_allowed) {
      issues.push('Investment advice enabled - requires proper licensing');
    }
    if (!settings.require_disclaimer) {
      issues.push('Disclaimers disabled - may violate transparency requirements');
    }

    // GDPR checks
    if (!settings.mask_personal_identifiers && !settings.mask_financial_data) {
      issues.push('PII masking largely disabled - review data minimization');
    }
    if (!settings.scan_output_for_pii) {
      issues.push('Output PII scanning disabled - risk of data leakage');
    }
    if (!settings.anonymize_logs) {
      issues.push('Log anonymization disabled - review data retention');
    }

    return {
      euAiActCompliant: settings.eu_ai_act_compliant && issues.length === 0,
      gdprCompliant: settings.gdpr_compliant && issues.length === 0,
      issues,
    };
  }

  /**
   * Validate custom instructions for potential issues
   */
  validateCustomInstructions(instructions: string): {
    valid: boolean;
    warnings: string[];
  } {
    const warnings: string[] = [];

    // Check for potential prompt injection patterns
    const dangerousPatterns = [
      /ignore\s+(previous|all|above)\s+instructions?/i,
      /forget\s+(your|all|previous)\s+(instructions?|rules?)/i,
      /disregard\s+(your|all|previous)\s+(instructions?|rules?)/i,
      /you\s+are\s+now\s+/i,
      /pretend\s+(to\s+be|you\s+are)/i,
      /bypass\s+(your|all|the)\s+(rules?|restrictions?|filters?)/i,
      /jailbreak/i,
      /DAN\s+mode/i,
    ];

    for (const pattern of dangerousPatterns) {
      if (pattern.test(instructions)) {
        warnings.push('Instructions contain potentially dangerous patterns');
        break;
      }
    }

    // Check length
    if (instructions.length > 5000) {
      warnings.push('Instructions exceed recommended length (5000 characters)');
    }

    return {
      valid: warnings.length === 0,
      warnings,
    };
  }

  /**
   * Calculate tokens estimate for custom instructions
   */
  estimateTokens(text: string): number {
    // Rough estimate: ~4 characters per token for English
    return Math.ceil(text.length / 4);
  }
}

export const aiSettingsService = new AISettingsService();
